import React from "react";
import { Check } from "lucide-react";

/**
 * OG image mockup gallery.
 * Each preview is rendered at 1200x630 (scaled down for display).
 * Shows the PropHero logo + tagline + 3 floating "live" cards around it:
 *   • Reserve Fund (top-right)
 *   • Proactive Property Management (top-left)
 *   • 24/7 Communication (bottom-right)
 */

const CARD_TESTID_PREFIX = "og-mock";

function Card({ children, testid, label }) {
  return (
    <figure className="mb-14" data-testid={`${CARD_TESTID_PREFIX}-${testid}`}>
      <figcaption className="mb-3 text-[13px] tracking-widest uppercase text-neutral-500 font-semibold">{label}</figcaption>
      <div
        className="mx-auto shadow-xl rounded-xl overflow-hidden border border-neutral-200"
        style={{ width: 1200, height: 630, transform: "scale(0.55)", transformOrigin: "top left" }}
      >
        {children}
      </div>
    </figure>
  );
}

function ReserveFundCard({ tone = "light" }) {
  const bg = tone === "glass" ? "rgba(255,255,255,0.94)" : "#FFFFFF";
  return (
    <div className="rounded-2xl border border-black/5 px-6 py-4" style={{ background: bg, width: 300, boxShadow: "0 20px 40px -20px rgba(0,0,0,0.15)" }}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-neutral-500">Reserve Fund</div>
        <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#023CFA" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#023CFA" }} />
          Live
        </div>
      </div>
      <div className="mt-2 text-[28px] font-semibold tracking-tight text-neutral-900">$1,284,320</div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-neutral-200 overflow-hidden">
        <div className="h-full rounded-full" style={{ background: "#023CFA", width: "72%" }} />
      </div>
      <div className="mt-2 text-[12px] text-neutral-500">72% of 2026 target</div>
    </div>
  );
}

function ProactiveCard({ tone = "light" }) {
  const bg = tone === "glass" ? "rgba(255,255,255,0.94)" : "#FFFFFF";
  return (
    <div className="rounded-2xl border border-black/5 px-5 py-4" style={{ background: bg, width: 280, boxShadow: "0 20px 40px -20px rgba(0,0,0,0.15)" }}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-neutral-500">Proactive Management</div>
        <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#023CFA" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#023CFA" }} />
          Live
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-[26px] font-semibold tracking-tight text-neutral-900">204</span>
        <span className="text-[13px] text-neutral-500">issues resolved</span>
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        {[92, 78, 88, 65, 96, 82, 70].map((v, i) => (
          <span
            key={i}
            className="rounded-sm"
            style={{ width: 8, height: `${(v / 100) * 20}px`, background: "#023CFA", opacity: 0.55 + (i / 20) }}
          />
        ))}
        <span className="ml-2 text-[12px] text-neutral-500">this month</span>
      </div>
    </div>
  );
}

function SupportCard({ tone = "light" }) {
  const bg = tone === "glass" ? "rgba(255,255,255,0.94)" : "#FFFFFF";
  return (
    <div className="rounded-2xl border border-black/5 px-5 py-4" style={{ background: bg, width: 280, boxShadow: "0 20px 40px -20px rgba(0,0,0,0.15)" }}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-neutral-500">24/7 Communication</div>
        <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#023CFA" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#023CFA" }} />
          Live
        </div>
      </div>
      <div className="mt-2 text-[26px] font-semibold tracking-tight text-neutral-900">3 min</div>
      <div className="mt-1 text-[13px] text-neutral-500">avg. response time</div>
      <div className="mt-3 flex items-center gap-2 text-[12px] text-neutral-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: "#16A34A" }} />
          Board · online
        </span>
        <span className="text-neutral-300">·</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: "#16A34A" }} />
          Residents · online
        </span>
      </div>
    </div>
  );
}

function LabelCard({ label, tone = "light" }) {
  const bg = tone === "glass" ? "rgba(255,255,255,0.94)" : "#FFFFFF";
  return (
    <div
      className="rounded-2xl border border-black/5 px-6 py-5 flex items-center justify-between gap-4"
      style={{ background: bg, width: 320, boxShadow: "0 20px 40px -20px rgba(0,0,0,0.15)" }}
    >
      <div className="text-[16px] font-semibold tracking-tight text-neutral-900" style={{ letterSpacing: "-0.01em" }}>{label}</div>
      <Check aria-hidden className="w-5 h-5" style={{ color: "#023CFA" }} strokeWidth={3} />
    </div>
  );
}

export default function OgPreviewMock() {
  return (
    <main data-testid="og-preview-mock" className="min-h-screen bg-neutral-100 py-14 px-6">
      <div className="max-w-5xl mx-auto mb-10">
        <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-semibold">OG preview · mockups</div>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-neutral-900 tracking-tight" style={{ fontFamily: `"SF Pro Display", -apple-system, sans-serif` }}>
          Pick a share-preview design
        </h1>
        <p className="mt-3 text-[15px] text-neutral-600 max-w-2xl">
          Logo centred; three live cards float around it — Proactive Management (top-left), Reserve Fund (top-right), 24/7 Communication (bottom-right). Each frame is exactly 1200×630 so it renders identically in iMessage, Twitter, LinkedIn.
        </p>
      </div>

      <div className="max-w-5xl mx-auto" style={{ height: 630 * 0.55 * 4 + 400 }}>
        {/* Option A — Clean white bg */}
        <Card testid="A" label="Option A · Clean white">
          <div className="w-full h-full relative bg-white">
            <div className="absolute top-8 left-8"><LabelCard label="24/7 Communication" /></div>
            <div className="absolute top-8 right-8"><ReserveFundCard /></div>
            <div className="absolute bottom-8 right-8"><LabelCard label="Proactive Property Management" /></div>
            <div className="w-full h-full flex items-center justify-center">
              <img src="/prophero-logo.png" alt="PropHero" style={{ height: 140 }} />
            </div>
          </div>
        </Card>

        {/* Option B — Soft grey */}
        <Card testid="B" label="Option B · Grey editorial">
          <div className="w-full h-full relative" style={{ background: "#F5F5F7" }}>
            <div className="absolute top-10 left-10"><LabelCard label="24/7 Communication" /></div>
            <div className="absolute top-10 right-10"><ReserveFundCard /></div>
            <div className="absolute bottom-10 right-10"><LabelCard label="Proactive Property Management" /></div>
            <div className="w-full h-full flex items-center justify-center">
              <img src="/prophero-logo.png" alt="PropHero" style={{ height: 140 }} />
            </div>
          </div>
        </Card>

        {/* Option C — Miami skyline with glass cards */}
        <Card testid="C" label="Option C · Miami skyline">
          <div className="w-full h-full relative">
            <img src="https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?w=1400&q=80" alt="Miami" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 100%)" }} />
            <div className="absolute top-10 left-10"><LabelCard label="24/7 Communication" tone="glass" /></div>
            <div className="absolute top-10 right-10"><ReserveFundCard tone="glass" /></div>
            <div className="absolute bottom-10 right-10"><LabelCard label="Proactive Property Management" tone="glass" /></div>
            <div className="w-full h-full relative flex items-center justify-center">
              <div className="inline-block bg-white rounded-2xl px-8 py-5" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.3)" }}>
                <img src="/prophero-logo.png" alt="PropHero" style={{ height: 92 }} />
              </div>
            </div>
          </div>
        </Card>

        {/* Option D — Brand blue */}
        <Card testid="D" label="Option D · Brand blue">
          <div className="w-full h-full relative" style={{ background: "linear-gradient(135deg, #023CFA 0%, #001FB0 100%)" }}>
            <div className="absolute top-10 left-10"><LabelCard label="24/7 Communication" /></div>
            <div className="absolute top-10 right-10"><ReserveFundCard /></div>
            <div className="absolute bottom-10 right-10"><LabelCard label="Proactive Property Management" /></div>
            <div className="w-full h-full flex items-center justify-center">
              <div className="inline-block bg-white rounded-2xl px-8 py-5" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.3)" }}>
                <img src="/prophero-logo.png" alt="PropHero" style={{ height: 92 }} />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </main>
  );
}

